import logger from "./utils/logger";
import { Suspense, lazy, useEffect } from "react";
import ErrorBoundary from "./ErrorBoundary";
import { persistor, store } from "./store";
import { Provider } from "react-redux";
import { PersistGate } from 'redux-persist/integration/react';
import SuspenseLoader from "./utils/SuspenseLoader";
import MyToast from "./utils/MyToast";
import { requestNotificationPermission } from "./utils/const";
import { Helmet } from "react-helmet";
import { captureError } from "./utils/monitoring";
/*
 * i18next is used directly here rather than the useTranslation hook: App renders the
 * <Suspense> boundary itself, so if App suspended while translations load there would be
 * no boundary above it to catch that. These strings are also produced inside event
 * handlers, where reading the current language at fire time is correct — the
 * non-reactivity that motivated the hook migration only affects rendered output.
 */
import i18next from "i18next";

const AppRouter = lazy(() => import("./Routes"));

function App() {
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  useEffect(() => {
    const requestPermissionWithUserGesture = () => {
      if ('safari' in window && 'pushNotification' in window.safari) {
        const permissionData = window.safari.pushNotification.permission('com.mywaymassage.dev');

        if (permissionData.permission === 'default') {
          try {
            window.safari.pushNotification.requestPermission(
              'https://dev.mywaymassage.com', // Your website URL
              'com.mywaymassage.dev', // Your website push ID
              {}, // Optional parameter object if needed
              function (newPermissionData) {
                if (newPermissionData.permission === 'granted') {
                  // Handle token here
                } else if (newPermissionData.permission === 'denied') {
                  logger.log('Permission denied');
                } else {
                  logger.log('Unexpected permission status:', newPermissionData.permission);
                }
              }
            );
          } catch (error) {
            captureError(error, { source: 'safariPushPermission' });
          }
        } else if (permissionData.permission === 'granted') {
          logger.log("Permission already granted.");
          // Handle token here
        } else if (permissionData.permission === 'denied') {
          logger.log("Permission already denied.");
        } else {
          logger.log("Unexpected initial permission status: " + permissionData.permission);
        }
      } else {
        logger.log("Safari Push Notifications are not supported in this browser.");
      }
    };
    requestPermissionWithUserGesture()
  }, []);


  /* Handling Net Status & Visible Title */

  // Function to handle visibility change
  const handleVisibilityChange = () => {
    if (document.hidden) {
      document.title = "My Way Massage";
    } else {
      document.title = "My Way Massage";
    }
  };

  useEffect(() => {
    const handleOnline = () => {
      MyToast("s", i18next.t("common.networkRestored"), "top-center");
    };

    const handleOffline = () => {
      MyToast("w", i18next.t("common.networkLost"), "top-center");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  /*
   * Global handlers for errors that never reach a React error boundary (async callbacks,
   * event handlers, rejected promises).
   *
   * This replaces a previous `window.onerror` hook that force-reloaded the page whenever
   * it saw "destroy is not a function" or "n is not a function". That hid a real bug
   * behind a reload — and risked a reload loop if the error recurred on mount. We now
   * report these instead, so the underlying fault is diagnosable rather than papered over.
   */
  useEffect(() => {
    const handleGlobalError = (event) => {
      captureError(event?.error || new Error(event?.message || 'Unknown window error'), {
        source: 'window.onerror',
        filename: event?.filename,
        lineno: event?.lineno,
      });
    };

    const handleRejection = (event) => {
      const reason = event?.reason;
      captureError(reason instanceof Error ? reason : new Error(String(reason)), {
        source: 'unhandledrejection',
      });
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);


  return (
    <>
      {/*
        NOTE (SEO): This Helmet block is mounted once at the app root, not per-route,
        so it cannot vary title/description/canonical per page. It intentionally does
        NOT declare og:image or JSON-LD anymore (both are handled once, correctly, as
        static tags in public/index.html) to avoid react-helmet overwriting the
        working head tags with duplicates/broken values on mount. True per-route meta
        would require adding <Helmet> to individual page components or prerendering.
      */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>My Way Massage | Book Massage Therapy Online, In-Home or In-Spa</title>
        <link rel="canonical" href="https://login.mywaymassage.com/" />
        <meta name="description" content="Book professional massage therapy on your terms — in-home or in-spa, standard or fully customized, with add-on enhancements and gift cards." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <Suspense fallback={<SuspenseLoader />}>
            <ErrorBoundary>
              <AppRouter />
            </ErrorBoundary>
          </Suspense>
        </PersistGate>
      </Provider>
    </>
  );
}

export default App;